import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { ClimbingBoxLoader } from 'react-spinners';
import Pagination from '../../components/Pagination';
import { Eye, WhatsappLogo, Check, Ticket, Trash, ClipboardText, CheckCircle, X, ArrowClockwise, Package, MusicNote, Link, Lightning, Hourglass, PaperPlaneRight } from '@phosphor-icons/react';
import './ICHarmonyAdmin.css';

const REGISTRATIONS_PER_PAGE = 10;

const ICHarmonyAdmin = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRegistrations, setTotalRegistrations] = useState(0);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showPreorderSection, setShowPreorderSection] = useState(false);
  
  // Security guard states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [securityCode, setSecurityCode] = useState('');
  const [securityError, setSecurityError] = useState('');

  const CORRECT_SECURITY_CODE = '12345678';

  // Fetch registrations function
  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      
      // Build query
      let query = supabase
        .from('ic_harmony_registrations')
        .select('*', { count: 'exact' });

      // Apply status filter
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      // Apply search
      if (searchQuery) {
        query = query.or(`nama_grup.ilike.%${searchQuery}%,nama_ketua.ilike.%${searchQuery}%,asal_instansi.ilike.%${searchQuery}%`);
      }

      // Get total count
      const { count: totalCount, error: countError } = await query;
      
      if (countError) throw countError;
      setTotalRegistrations(totalCount || 0);
      
      // Calculate pagination range
      const from = (currentPage - 1) * REGISTRATIONS_PER_PAGE;
      const to = from + REGISTRATIONS_PER_PAGE - 1;

      // Fetch paginated data
      let dataQuery = supabase
        .from('ic_harmony_registrations')
        .select('*');

      // Re-apply filters for data query
      if (statusFilter !== 'all') {
        dataQuery = dataQuery.eq('status', statusFilter);
      }

      if (searchQuery) {
        dataQuery = dataQuery.or(`nama_grup.ilike.%${searchQuery}%,nama_ketua.ilike.%${searchQuery}%,asal_instansi.ilike.%${searchQuery}%`);
      }

      const { data, error } = await dataQuery
        .order(sortBy, { ascending: sortOrder === 'asc' })
        .range(from, to);

      if (error) throw error;
      setRegistrations(data || []);
    } catch (error) {
      console.error('Error fetching registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when authenticated - MOVED BEFORE CONDITIONAL RETURN
  useEffect(() => {
    if (isAuthenticated) {
      fetchRegistrations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, currentPage, statusFilter, sortBy, sortOrder]);

  const handleSecuritySubmit = (e) => {
    e.preventDefault();
    if (securityCode === CORRECT_SECURITY_CODE) {
      setIsAuthenticated(true);
      setSecurityError('');
    } else {
      setSecurityError('Kode keamanan salah. Silakan coba lagi.');
    }
  };

  // Security Guard Screen
  if (!isAuthenticated) {
    return (
      <div className="ic-harmony-admin">
        <div className="security-guard-container">
          <div className="security-guard-card">
            <div className="security-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="M12 8v4"/>
                <path d="M12 16h.01"/>
              </svg>
            </div>
            <h2>Akses Admin IC Harmony</h2>
            <p className="security-description">
              Halaman ini dilindungi. Masukkan kode keamanan untuk melanjutkan.
            </p>
            
            <form onSubmit={handleSecuritySubmit} className="security-form">
              <div className="security-input-group">
                <input
                  type="password"
                  value={securityCode}
                  onChange={(e) => setSecurityCode(e.target.value)}
                  placeholder="Masukkan kode keamanan"
                  className="security-input"
                  maxLength={8}
                />
              </div>
              {securityError && (
                <p className="security-error">{securityError}</p>
              )}
              <button type="submit" className="security-submit-btn">
                Masuk Dashboard
              </button>
            </form>
            
            <p className="security-hint">
              Hubungi administrator jika Anda lupa kode keamanan.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchRegistrations();
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const { error } = await supabase
        .from('ic_harmony_registrations')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      
      // Update local state
      setRegistrations(registrations.map(reg => 
        reg.id === id ? { ...reg, status: newStatus } : reg
      ));
      
      if (selectedRegistration?.id === id) {
        setSelectedRegistration({ ...selectedRegistration, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Gagal mengupdate status');
    }
  };

  const handleQuickStatusUpdate = async (id, newStatus) => {
    try {
      const { error } = await supabase
        .from('ic_harmony_registrations')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      
      setRegistrations(registrations.map(reg => 
        reg.id === id ? { ...reg, status: newStatus } : reg
      ));
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Gagal mengupdate status');
    }
  };

  const handleMerchandisePickupUpdate = async (id, pickupStatus, namaPengambil = null) => {
    try {
      const updateData = { 
        merchandise_pickup_status: pickupStatus, 
        updated_at: new Date().toISOString() 
      };
      
      // If status is picked_up, automatically set tanggal_pengambilan
      if (pickupStatus === 'picked_up') {
        updateData.tanggal_pengambilan = new Date().toISOString();
      }
      
      // If nama_pengambil is provided, update it
      if (namaPengambil !== null) {
        updateData.nama_pengambil = namaPengambil;
      }
      
      const { error } = await supabase
        .from('ic_harmony_registrations')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
      
      setRegistrations(registrations.map(reg => 
        reg.id === id ? { ...reg, ...updateData } : reg
      ));
      
      if (selectedRegistration?.id === id) {
        setSelectedRegistration({ ...selectedRegistration, ...updateData });
      }
    } catch (error) {
      console.error('Error updating merchandise pickup status:', error);
      alert('Gagal mengupdate status pengambilan merchandise');
    }
  };

  const handleMusicMinusOneUpdate = async (id, status, link = null) => {
    try {
      const updateData = { 
        music_minus_one_status: status, 
        updated_at: new Date().toISOString() 
      };
      if (link !== null) updateData.music_minus_one_link = link;
      
      const { error } = await supabase
        .from('ic_harmony_registrations')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
      
      setRegistrations(registrations.map(reg => 
        reg.id === id ? { ...reg, ...updateData } : reg
      ));
      
      if (selectedRegistration?.id === id) {
        setSelectedRegistration({ ...selectedRegistration, ...updateData });
      }
    } catch (error) {
      console.error('Error updating music minus one status:', error);
      alert('Gagal mengupdate status file music minus one');
    }
  };

  const exportToCSV = () => {
    const headers = ['No', 'Nama Grup', 'Jenis Lomba', 'Nama Ketua', 'No WhatsApp', 'Email', 'Asal Instansi', 'Jumlah Anggota', 'Jersey Design', 'Jersey Details', 'Topi', 'Total Biaya', 'Status', 'Tanggal Daftar'];
    
    const rows = registrations.map((reg, index) => {
      const jerseyDetails = reg.jersey_details?.map(d => `${d.ukuran}:${d.jumlah}`).join('; ') || '';
      return [
        (currentPage - 1) * REGISTRATIONS_PER_PAGE + index + 1,
        reg.nama_grup,
        reg.jenis_lomba,
        reg.nama_ketua,
        reg.no_whatsapp,
        reg.email || '-',
        reg.asal_instansi,
        reg.jumlah_anggota,
        reg.jersey_design || '-',
        jerseyDetails,
        reg.topi_pre_order ? reg.topi_jumlah : 0,
        reg.total_biaya,
        getStatusLabel(reg.status),
        new Date(reg.created_at).toLocaleString('id-ID')
      ];
    });
    
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `ic_harmony_registrations_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus pendaftaran ini?')) {
      try {
        const { error } = await supabase
          .from('ic_harmony_registrations')
          .delete()
          .eq('id', id);

        if (error) throw error;
        
        setRegistrations(registrations.filter(reg => reg.id !== id));
        setShowDetailModal(false);
        
        // Refresh count and pagination
        const { count, error: countError } = await supabase
          .from('ic_harmony_registrations')
          .select('*', { count: 'exact', head: true });
          
        if (!countError) {
          setTotalRegistrations(count);
          const totalPages = Math.ceil(count / REGISTRATIONS_PER_PAGE);
          if (currentPage > totalPages && currentPage > 1) {
            setCurrentPage(currentPage - 1);
          }
        }
      } catch (error) {
        console.error('Error deleting registration:', error);
        alert('Gagal menghapus pendaftaran');
      }
    }
  };

  const openDetailModal = (registration) => {
    setSelectedRegistration(registration);
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedRegistration(null);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'paid':
        return 'status-paid';
      case 'confirmed':
        return 'status-confirmed';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return 'status-pending';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'paid':
        return 'Sudah Bayar';
      case 'confirmed':
        return 'Terkonfirmasi';
      case 'cancelled':
        return 'Dibatalkan';
      default:
        return 'Menunggu';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getWhatsAppLink = (phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    const formattedPhone = cleanPhone.startsWith('0') ? '62' + cleanPhone.slice(1) : cleanPhone;
    return `https://wa.me/${formattedPhone}`;
  };

  // Calculate pre-order statistics
  const preorderStats = {
    jerseyDesign1: registrations.filter(r => r.jersey_design === 'design1').reduce((sum, r) => 
      sum + (r.jersey_details?.reduce((total, item) => total + (parseInt(item.jumlah) || 0), 0) || 0), 0),
    jerseyDesign2: registrations.filter(r => r.jersey_design === 'design2').reduce((sum, r) => 
      sum + (r.jersey_details?.reduce((total, item) => total + (parseInt(item.jumlah) || 0), 0) || 0), 0),
    topi: registrations.filter(r => r.topi_pre_order).reduce((sum, r) => sum + (parseInt(r.topi_jumlah) || 0), 0),
    totalRevenue: registrations.reduce((sum, r) => {
      const jerseyCost = r.jersey_design 
        ? (r.jersey_details?.reduce((total, item) => total + (parseInt(item.jumlah) || 0), 0) || 0) * 115000 
        : 0;
      const topiCost = r.topi_pre_order ? (parseInt(r.topi_jumlah) || 0) * 115000 : 0;
      return sum + jerseyCost + topiCost;
    }, 0)
  };

  // Get jersey size breakdown per design
  const jerseySizeBreakdownByDesign = registrations.reduce((acc, r) => {
    if (r.jersey_details && r.jersey_design) {
      r.jersey_details.forEach(item => {
        if (!acc[r.jersey_design]) acc[r.jersey_design] = {};
        acc[r.jersey_design][item.ukuran] = (acc[r.jersey_design][item.ukuran] || 0) + (parseInt(item.jumlah) || 0);
      });
    }
    return acc;
  }, {});

  // Get total jersey count per design
  const jerseyCountByDesign = {
    design1: registrations.filter(r => r.jersey_design === 'design1').reduce((sum, r) => 
      sum + (r.jersey_details?.reduce((total, item) => total + (parseInt(item.jumlah) || 0), 0) || 0), 0),
    design2: registrations.filter(r => r.jersey_design === 'design2').reduce((sum, r) => 
      sum + (r.jersey_details?.reduce((total, item) => total + (parseInt(item.jumlah) || 0), 0) || 0), 0)
  };

  const preorderRegistrations = registrations.filter(r => r.jersey_design || r.topi_pre_order);

  // Financial Statistics
  const financialStats = {
    totalRegistrationFees: registrations.reduce((sum, r) => {
      const baseFee = r.jenis_lomba === 'hadroh' ? 150000 : 75000;
      return sum + baseFee;
    }, 0),
    totalMerchandiseRevenue: preorderStats.totalRevenue,
    totalExpectedRevenue: registrations.reduce((sum, r) => sum + (r.total_biaya || 0), 0),
    paidAmount: registrations
      .filter(r => r.status === 'paid' || r.status === 'confirmed')
      .reduce((sum, r) => sum + (r.total_biaya || 0), 0),
    pendingAmount: registrations
      .filter(r => r.status === 'pending')
      .reduce((sum, r) => sum + (r.total_biaya || 0), 0)
  };

  // Category Statistics
  const categoryStats = {
    hadroh: registrations.filter(r => r.jenis_lomba === 'hadroh').length,
    nasyid: registrations.filter(r => r.jenis_lomba === 'nasyid').length,
    hadrohRevenue: registrations
      .filter(r => r.jenis_lomba === 'hadroh')
      .reduce((sum, r) => sum + (r.total_biaya || 0), 0),
    nasyidRevenue: registrations
      .filter(r => r.jenis_lomba === 'nasyid')
      .reduce((sum, r) => sum + (r.total_biaya || 0), 0)
  };

  // Daily Statistics
  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
  
  const dailyStats = {
    today: registrations.filter(r => r.created_at?.startsWith(today)).length,
    yesterday: registrations.filter(r => r.created_at?.startsWith(yesterday)).length,
    thisWeek: registrations.filter(r => {
      const regDate = new Date(r.created_at);
      const weekAgo = new Date(Date.now() - 7 * 86400000);
      return regDate >= weekAgo;
    }).length
  };

  return (
    <div className="ic-harmony-admin">
      {/* Header with Actions */}
      <div className="dashboard-header">
        <h2>📊 Dashboard IC Harmony 2026</h2>
        <div className="header-actions">
          <button onClick={fetchRegistrations} className="refresh-button" title="Refresh Data">
            🔄 Refresh
          </button>
          <button onClick={exportToCSV} className="export-button" title="Export ke CSV">
            📥 Export CSV
          </button>
        </div>
      </div>

      {/* Quick Daily Stats */}
      <div className="daily-stats-bar">
        <div className="daily-stat">
          <span className="daily-label">Hari Ini</span>
          <span className="daily-value highlight">+{dailyStats.today}</span>
        </div>
        <div className="daily-stat">
          <span className="daily-label">Kemarin</span>
          <span className="daily-value">+{dailyStats.yesterday}</span>
        </div>
        <div className="daily-stat">
          <span className="daily-label">7 Hari Terakhir</span>
          <span className="daily-value">+{dailyStats.thisWeek}</span>
        </div>
      </div>

      {/* Financial Overview */}
      <div className="financial-dashboard">
        <h3 className="section-title">💰 Ringkasan Keuangan</h3>
        <div className="financial-cards">
          <div className="financial-card total-revenue">
            <div className="financial-icon">💵</div>
            <div className="financial-info">
              <h4>Total Pendapatan Diharapkan</h4>
              <p className="financial-value">{formatCurrency(financialStats.totalExpectedRevenue)}</p>
              <p className="financial-label">Dari {totalRegistrations} pendaftar</p>
            </div>
          </div>
          
          <div className="financial-card paid">
            <div className="financial-icon">✅</div>
            <div className="financial-info">
              <h4>Sudah Dibayar</h4>
              <p className="financial-value">{formatCurrency(financialStats.paidAmount)}</p>
              <p className="financial-label">
                {registrations.filter(r => r.status === 'paid' || r.status === 'confirmed').length} pendaftar
              </p>
            </div>
          </div>
          
          <div className="financial-card pending">
            <div className="financial-icon">⏳</div>
            <div className="financial-info">
              <h4>Menunggu Pembayaran</h4>
              <p className="financial-value">{formatCurrency(financialStats.pendingAmount)}</p>
              <p className="financial-label">
                {registrations.filter(r => r.status === 'pending').length} pendaftar
              </p>
            </div>
          </div>
          
          <div className="financial-card registration-fees">
            <div className="financial-icon">🎫</div>
            <div className="financial-info">
              <h4>Total Biaya Pendaftaran</h4>
              <p className="financial-value">{formatCurrency(financialStats.totalRegistrationFees)}</p>
              <p className="financial-label">Base registration only</p>
            </div>
          </div>
          
          <div className="financial-card merchandise-revenue">
            <div className="financial-icon">👕</div>
            <div className="financial-info">
              <h4>Total Merchandise</h4>
              <p className="financial-value">{formatCurrency(financialStats.totalMerchandiseRevenue)}</p>
              <p className="financial-label">{preorderRegistrations.length} pre-orders</p>
            </div>
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="category-dashboard">
        <h3 className="section-title">🏆 Statistik Kategori Lomba</h3>
        <div className="category-cards">
          <div className="category-card hadroh">
            <div className="category-icon">🥁</div>
            <div className="category-info">
              <h4>Festival Hadroh</h4>
              <div className="category-stats-row">
                <div>
                  <p className="category-count">{categoryStats.hadroh}</p>
                  <p className="category-label">Grup</p>
                </div>
                <div>
                  <p className="category-revenue">{formatCurrency(categoryStats.hadrohRevenue)}</p>
                  <p className="category-label">Revenue</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="category-card nasyid">
            <div className="category-icon">🎤</div>
            <div className="category-info">
              <h4>Festival Nasyid</h4>
              <div className="category-stats-row">
                <div>
                  <p className="category-count">{categoryStats.nasyid}</p>
                  <p className="category-label">Grup</p>
                </div>
                <div>
                  <p className="category-revenue">{formatCurrency(categoryStats.nasyidRevenue)}</p>
                  <p className="category-label">Revenue</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Overview */}
      <div className="status-overview">
        <h3 className="section-title">📈 Status Pendaftaran</h3>
        <div className="status-bars">
          <div className="status-bar-item">
            <div className="status-bar-header">
              <span className="status-label-bar">Terkonfirmasi</span>
              <span className="status-value-bar">{registrations.filter(r => r.status === 'confirmed').length}</span>
            </div>
            <div className="status-bar-track">
              <div 
                className="status-bar-fill confirmed" 
                style={{ width: `${totalRegistrations > 0 ? (registrations.filter(r => r.status === 'confirmed').length / totalRegistrations * 100) : 0}%` }}
              />
            </div>
          </div>
          
          <div className="status-bar-item">
            <div className="status-bar-header">
              <span className="status-label-bar">Sudah Bayar</span>
              <span className="status-value-bar">{registrations.filter(r => r.status === 'paid').length}</span>
            </div>
            <div className="status-bar-track">
              <div 
                className="status-bar-fill paid" 
                style={{ width: `${totalRegistrations > 0 ? (registrations.filter(r => r.status === 'paid').length / totalRegistrations * 100) : 0}%` }}
              />
            </div>
          </div>
          
          <div className="status-bar-item">
            <div className="status-bar-header">
              <span className="status-label-bar">Menunggu</span>
              <span className="status-value-bar">{registrations.filter(r => r.status === 'pending').length}</span>
            </div>
            <div className="status-bar-track">
              <div 
                className="status-bar-fill pending" 
                style={{ width: `${totalRegistrations > 0 ? (registrations.filter(r => r.status === 'pending').length / totalRegistrations * 100) : 0}%` }}
              />
            </div>
          </div>
          
          <div className="status-bar-item">
            <div className="status-bar-header">
              <span className="status-label-bar">Dibatalkan</span>
              <span className="status-value-bar">{registrations.filter(r => r.status === 'cancelled').length}</span>
            </div>
            <div className="status-bar-track">
              <div 
                className="status-bar-fill cancelled" 
                style={{ width: `${totalRegistrations > 0 ? (registrations.filter(r => r.status === 'cancelled').length / totalRegistrations * 100) : 0}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Header Stats - Original */}
      <div className="stats-container">
        <div className="stat-card">
          <h3>Total Pendaftar</h3>
          <p className="stat-value">{totalRegistrations}</p>
        </div>
        <div className="stat-card pending">
          <h3>Menunggu</h3>
          <p className="stat-value">
            {registrations.filter(r => r.status === 'pending').length}
          </p>
        </div>
        <div className="stat-card paid">
          <h3>Sudah Bayar</h3>
          <p className="stat-value">
            {registrations.filter(r => r.status === 'paid').length}
          </p>
        </div>
        <div className="stat-card confirmed">
          <h3>Terkonfirmasi</h3>
          <p className="stat-value">
            {registrations.filter(r => r.status === 'confirmed').length}
          </p>
        </div>
      </div>

      {/* Pre-order Stats */}
      <div className="preorder-stats-container">
        <div className="preorder-header">
          <h3>📦 Ringkasan Pre-Order Merchandise</h3>
          <button 
            onClick={() => setShowPreorderSection(!showPreorderSection)}
            className="toggle-preorder-btn"
          >
            {showPreorderSection ? 'Sembunyikan' : 'Lihat Detail'}
          </button>
        </div>
        
        <div className="preorder-cards">
          <div className="preorder-card jersey-d1">
            <div className="preorder-icon">👕</div>
            <div className="preorder-info">
              <h4>Jersey Design 1</h4>
              <p className="preorder-count">{preorderStats.jerseyDesign1} pcs</p>
              <p className="preorder-label">Putih Hijau</p>
            </div>
          </div>
          
          <div className="preorder-card jersey-d2">
            <div className="preorder-icon">👕</div>
            <div className="preorder-info">
              <h4>Jersey Design 2</h4>
              <p className="preorder-count">{preorderStats.jerseyDesign2} pcs</p>
              <p className="preorder-label">Hitam Gold</p>
            </div>
          </div>
          
          <div className="preorder-card topi">
            <div className="preorder-icon">🧢</div>
            <div className="preorder-info">
              <h4>Topi Event</h4>
              <p className="preorder-count">{preorderStats.topi} pcs</p>
              <p className="preorder-label">Premium</p>
            </div>
          </div>
          
          <div className="preorder-card revenue">
            <div className="preorder-icon">💰</div>
            <div className="preorder-info">
              <h4>Total Pre-Order</h4>
              <p className="preorder-count">{formatCurrency(preorderStats.totalRevenue)}</p>
              <p className="preorder-label">Dari {preorderRegistrations.length} pendaftar</p>
            </div>
          </div>
        </div>

        {/* Detailed Pre-order Section */}
        {showPreorderSection && (
          <div className="preorder-details">
            {/* Jersey Size Breakdown Per Design */}
            {(preorderStats.jerseyDesign1 > 0 || preorderStats.jerseyDesign2 > 0) && (
              <div className="preorder-section">
                <h4>📊 Breakdown Ukuran Jersey per Design</h4>
                
                {/* Design 1 Breakdown */}
                {jerseySizeBreakdownByDesign.design1 && Object.keys(jerseySizeBreakdownByDesign.design1).length > 0 && (
                  <div className="design-breakdown design-1">
                    <h5>Design 1 (Putih Hijau) - {jerseyCountByDesign.design1} pcs</h5>
                    <div className="size-breakdown">
                      {Object.entries(jerseySizeBreakdownByDesign.design1).map(([size, count]) => (
                        <div key={`d1-${size}`} className="size-item">
                          <span className="size-label">{size}</span>
                          <span className="size-count">{count} pcs</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Design 2 Breakdown */}
                {jerseySizeBreakdownByDesign.design2 && Object.keys(jerseySizeBreakdownByDesign.design2).length > 0 && (
                  <div className="design-breakdown design-2">
                    <h5>Design 2 (Hitam Gold) - {jerseyCountByDesign.design2} pcs</h5>
                    <div className="size-breakdown">
                      {Object.entries(jerseySizeBreakdownByDesign.design2).map(([size, count]) => (
                        <div key={`d2-${size}`} className="size-item">
                          <span className="size-label">{size}</span>
                          <span className="size-count">{count} pcs</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Pre-order Table */}
            {preorderRegistrations.length > 0 && (
              <div className="preorder-table-section">
                <h4>📋 Daftar Pre-Order</h4>
                <div className="table-container preorder-table-container">
                  <table className="registrations-table preorder-table">
                    <thead>
                      <tr>
                        <th>No</th>
                        <th>Nama Grup</th>
                        <th>Jersey D1</th>
                        <th>Jersey D2</th>
                        <th>Topi</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preorderRegistrations.map((reg, index) => {
                        const d1Count = reg.jersey_design === 'design1' 
                          ? reg.jersey_details?.reduce((sum, item) => sum + (parseInt(item.jumlah) || 0), 0) || 0 
                          : 0;
                        const d2Count = reg.jersey_design === 'design2' 
                          ? reg.jersey_details?.reduce((sum, item) => sum + (parseInt(item.jumlah) || 0), 0) || 0 
                          : 0;
                        const topiCount = reg.topi_pre_order ? parseInt(reg.topi_jumlah) || 0 : 0;
                        const preorderTotal = ((d1Count + d2Count) * 115000) + (topiCount * 115000);
                        
                        return (
                          <tr key={reg.id}>
                            <td>{index + 1}</td>
                            <td className="font-medium">{reg.nama_grup}</td>
                            <td>{d1Count > 0 ? `${d1Count} pcs` : '-'}</td>
                            <td>{d2Count > 0 ? `${d2Count} pcs` : '-'}</td>
                            <td>{topiCount > 0 ? `${topiCount} pcs` : '-'}</td>
                            <td className="font-medium">{formatCurrency(preorderTotal)}</td>
                            <td>
                              <span className={`preorder-status ${getStatusBadgeClass(reg.status)}`}>
                                {getStatusLabel(reg.status)}
                              </span>
                            </td>
                            <td>
                              <button
                                onClick={() => openDetailModal(reg)}
                                className="action-button view-button small"
                              >
                                👁
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="filters-container">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Cari nama grup, ketua, atau instansi..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">
            🔍 Cari
          </button>
        </form>

        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="filter-select"
          >
            <option value="all">Semua Status</option>
            <option value="pending">Menunggu</option>
            <option value="paid">Sudah Bayar</option>
            <option value="confirmed">Terkonfirmasi</option>
            <option value="cancelled">Dibatalkan</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="filter-select"
          >
            <option value="created_at">Tanggal Daftar</option>
            <option value="nama_grup">Nama Grup</option>
            <option value="jenis_lomba">Jenis Lomba</option>
            <option value="status">Status</option>
          </select>

          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="sort-button"
          >
            {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="loading-container">
          <ClimbingBoxLoader color='var(--primary-color)' size={10} />
        </div>
      ) : (
        <>
          {/* Registrations Cards */}
          {registrations.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><ClipboardText size={48} weight="thin" /></div>
              <p>Tidak ada pendaftaran ditemukan</p>
            </div>
          ) : (
            <div className="cards-container">
              {registrations.map((reg, index) => (
                <div key={reg.id} className="participant-card">
                  <div className="card-header">
                    <div className="card-number">{(currentPage - 1) * REGISTRATIONS_PER_PAGE + index + 1}</div>
                    <div className="card-category">
                      <span className={`lomba-badge ${reg.jenis_lomba}`}>
                        {reg.jenis_lomba === 'hadroh' ? 'Hadroh' : 'Nasyid'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="card-body">
                    <h3 className="card-title">{reg.nama_grup}</h3>
                    <p className="card-subtitle">{reg.asal_instansi}</p>
                    
                    <div className="card-info-grid">
                      <div className="info-item">
                        <label>Ketua</label>
                        <span>{reg.nama_ketua}</span>
                      </div>
                      <div className="info-item">
                        <label>WhatsApp</label>
                        <span>{reg.no_whatsapp}</span>
                      </div>
                      <div className="info-item">
                        <label>Biaya</label>
                        <span className="amount">{formatCurrency(reg.total_biaya)}</span>
                      </div>
                      <div className="info-item">
                        <label>Tanggal</label>
                        <span>{new Date(reg.created_at).toLocaleDateString('id-ID')}</span>
                      </div>
                    </div>
                    
                    {reg.jenis_lomba === 'nasyid' && (
                      <div className="card-minus-one">
                        <label>Minus One:</label>
                        <select
                          value={reg.music_minus_one_status || 'not_submitted'}
                          onChange={(e) => handleMusicMinusOneUpdate(reg.id, e.target.value)}
                          className={`inline-status-select ${reg.music_minus_one_status || 'not_submitted'}`}
                        >
                          <option value="not_submitted">Belum Dikirim</option>
                          <option value="submitted">Sudah Dikirim</option>
                          <option value="verified">Terverifikasi</option>
                        </select>
                      </div>
                    )}
                    
                    {(reg.jersey_design || reg.topi_pre_order) && (
                      <div className="card-pickup-info">
                        <div className="pickup-status-row">
                          <label>Pengambilan:</label>
                          <span className={`pickup-badge ${reg.merchandise_pickup_status || 'not_ready'}`}>
                            {reg.merchandise_pickup_status === 'picked_up' ? 'Sudah Diambil' : 
                             reg.merchandise_pickup_status === 'ready' ? 'Siap' : 'Belum Siap'}
                          </span>
                        </div>
                        {reg.nama_pengambil && (
                          <div className="pengambil-info">
                            <span className="pengambil-label">Diambil oleh:</span>
                            <span className="pengambil-name">{reg.nama_pengambil}</span>
                            {reg.tanggal_pengambilan && (
                              <span className="pengambil-date">
                                {new Date(reg.tanggal_pengambilan).toLocaleDateString('id-ID')}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="card-footer">
                    <select
                      value={reg.status}
                      onChange={(e) => handleStatusChange(reg.id, e.target.value)}
                      className={`card-status-select ${getStatusBadgeClass(reg.status)}`}
                    >
                      <option value="pending">Menunggu</option>
                      <option value="paid">Sudah Bayar</option>
                      <option value="confirmed">Terkonfirmasi</option>
                      <option value="cancelled">Dibatalkan</option>
                    </select>
                    
                    <div className="card-actions">
                      <button
                        onClick={() => openDetailModal(reg)}
                        className="card-btn view"
                        title="Detail"
                      >
                        <Eye size={16} weight="bold" />
                      </button>
                      <a
                        href={getWhatsAppLink(reg.no_whatsapp)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="card-btn wa"
                        title="WhatsApp"
                      >
                        <WhatsappLogo size={16} weight="fill" />
                      </a>
                      {reg.status === 'pending' && (
                        <button
                          onClick={() => handleQuickStatusUpdate(reg.id, 'paid')}
                          className="card-btn quick-paid"
                          title="Bayar"
                        >
                          <Check size={16} weight="bold" />
                        </button>
                      )}
                      {reg.status === 'paid' && (
                        <button
                          onClick={() => handleQuickStatusUpdate(reg.id, 'confirmed')}
                          className="card-btn quick-confirm"
                          title="Konfirm"
                        >
                          <Ticket size={16} weight="fill" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(reg.id)}
                        className="card-btn delete"
                        title="Hapus"
                      >
                        <Trash size={16} weight="bold" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {registrations.length > 0 && totalRegistrations > REGISTRATIONS_PER_PAGE && (
            <Pagination
              currentPage={currentPage}
              totalItems={totalRegistrations}
              itemsPerPage={REGISTRATIONS_PER_PAGE}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRegistration && (
        <div className="modal-overlay" onClick={closeDetailModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detail Pendaftaran</h2>
              <button onClick={closeDetailModal} className="close-button">×</button>
            </div>
            
            <div className="modal-body">
              <div className="detail-section">
                <h3>Informasi Grup</h3>
                <div className="detail-grid">
                  <div>
                    <label>Nama Grup:</label>
                    <p>{selectedRegistration.nama_grup}</p>
                  </div>
                  <div>
                    <label>Jenis Lomba:</label>
                    <p>{selectedRegistration.jenis_lomba === 'hadroh' ? 'Festival Hadroh' : 'Festival Nasyid'}</p>
                  </div>
                  <div>
                    <label>Asal Instansi:</label>
                    <p>{selectedRegistration.asal_instansi}</p>
                  </div>
                  <div>
                    <label>Jumlah Anggota:</label>
                    <p>{selectedRegistration.jumlah_anggota} orang</p>
                  </div>
                </div>
              </div>

              {/* Song Selection Info */}
              <div className="detail-section">
                <h3><MusicNote size={18} weight="fill" style={{verticalAlign: 'middle', marginRight: '6px'}} /> Pilihan Lagu</h3>
                <div className="detail-grid">
                  <div>
                    <label>Lagu Wajib:</label>
                    <p className="font-medium text-[#1a4d2e]">{selectedRegistration.lagu_wajib || '-'}</p>
                  </div>
                  <div>
                    <label>Lagu Pilihan:</label>
                    <p className="font-medium text-[#c9a227]">{selectedRegistration.lagu_pilihan || '-'}</p>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Informasi Ketua</h3>
                <div className="detail-grid">
                  <div>
                    <label>Nama Ketua:</label>
                    <p>{selectedRegistration.nama_ketua}</p>
                  </div>
                  <div>
                    <label>No. WhatsApp:</label>
                    <div className="contact-row">
                      <p>{selectedRegistration.no_whatsapp}</p>
                      <a
                        href={getWhatsAppLink(selectedRegistration.no_whatsapp)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="wa-link-button"
                      >
                        <WhatsappLogo size={14} weight="fill" style={{marginRight: '4px'}} /> Chat WhatsApp
                      </a>
                    </div>
                  </div>
                  <div>
                    <label>Email:</label>
                    <p>{selectedRegistration.email || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="detail-section quick-actions-section">
                <h3><Lightning size={18} weight="fill" style={{verticalAlign: 'middle', marginRight: '6px'}} /> Aksi Cepat</h3>
                <div className="quick-actions">
                  {selectedRegistration.status === 'pending' && (
                    <button
                      onClick={() => {
                        handleStatusChange(selectedRegistration.id, 'paid');
                        setSelectedRegistration({ ...selectedRegistration, status: 'paid' });
                      }}
                      className="quick-action-btn paid-btn"
                    >
                      <CheckCircle size={16} weight="fill" style={{marginRight: '4px', verticalAlign: 'middle'}} /> Tandai Sudah Bayar
                    </button>
                  )}
                  {selectedRegistration.status === 'paid' && (
                    <button
                      onClick={() => {
                        handleStatusChange(selectedRegistration.id, 'confirmed');
                        setSelectedRegistration({ ...selectedRegistration, status: 'confirmed' });
                      }}
                      className="quick-action-btn confirmed-btn"
                    >
                      <Ticket size={16} weight="fill" style={{marginRight: '4px', verticalAlign: 'middle'}} /> Konfirmasi Pendaftaran
                    </button>
                  )}
                  {selectedRegistration.status !== 'cancelled' && (
                    <button
                      onClick={() => {
                        handleStatusChange(selectedRegistration.id, 'cancelled');
                        setSelectedRegistration({ ...selectedRegistration, status: 'cancelled' });
                      }}
                      className="quick-action-btn cancelled-btn"
                    >
                      <X size={16} weight="bold" style={{marginRight: '4px', verticalAlign: 'middle'}} /> Batalkan Pendaftaran
                    </button>
                  )}
                  {selectedRegistration.status === 'cancelled' && (
                    <button
                      onClick={() => {
                        handleStatusChange(selectedRegistration.id, 'pending');
                        setSelectedRegistration({ ...selectedRegistration, status: 'pending' });
                      }}
                      className="quick-action-btn pending-btn"
                    >
                      <ArrowClockwise size={16} weight="bold" style={{marginRight: '4px', verticalAlign: 'middle'}} /> Aktifkan Kembali
                    </button>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h3>Pesanan Merchandise</h3>
                {selectedRegistration.jersey_design && selectedRegistration.jersey_details?.length > 0 ? (
                  <div className="merchandise-item">
                    <p><strong>Jersey {selectedRegistration.jersey_design === 'design1' ? 'Design 1 (Putih Hijau)' : 'Design 2 (Hitam Gold)'}</strong></p>
                    <ul>
                      {selectedRegistration.jersey_details.map((detail, idx) => (
                        <li key={idx}>Ukuran {detail.ukuran}: {detail.jumlah} pcs</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                
                {selectedRegistration.topi_pre_order && selectedRegistration.topi_jumlah > 0 ? (
                  <div className="merchandise-item">
                    <p><strong>Topi Event:</strong> {selectedRegistration.topi_jumlah} pcs</p>
                  </div>
                ) : null}
                
                {!selectedRegistration.jersey_design && !selectedRegistration.topi_pre_order && (
                  <p className="text-muted">Tidak ada pesanan merchandise</p>
                )}

                {/* Merchandise Pickup Status */}
                {(selectedRegistration.jersey_design || selectedRegistration.topi_pre_order) && (
                  <div className="pickup-status-section mt-4 pt-4 border-t">
                    <h4 className="font-semibold mb-3"><Package size={16} weight="fill" style={{verticalAlign: 'middle', marginRight: '4px'}} /> Status Pengambilan Barang</h4>
                    <div className="pickup-controls">
                      <select
                        value={selectedRegistration.merchandise_pickup_status || 'not_ready'}
                        onChange={(e) => handleMerchandisePickupUpdate(selectedRegistration.id, e.target.value, selectedRegistration.nama_pengambil)}
                        className={`pickup-status-select ${selectedRegistration.merchandise_pickup_status || 'not_ready'}`}
                      >
                        <option value="not_ready">Belum Siap</option>
                        <option value="ready">Siap Diambil</option>
                        <option value="picked_up">Sudah Diambil</option>
                      </select>
                      <span className={`pickup-status-badge ${selectedRegistration.merchandise_pickup_status || 'not_ready'}`}>
                        {selectedRegistration.merchandise_pickup_status === 'picked_up' ? <><CheckCircle size={14} weight="fill" style={{verticalAlign: 'middle', marginRight: '4px'}} /> Sudah Diambil</> : 
                         selectedRegistration.merchandise_pickup_status === 'ready' ? <><Package size={14} weight="fill" style={{verticalAlign: 'middle', marginRight: '4px'}} /> Siap Diambil</> : 
                         <><Hourglass size={14} weight="fill" style={{verticalAlign: 'middle', marginRight: '4px'}} /> Belum Siap</>}
                      </span>
                    </div>
                    
                    {/* Nama Pengambil Input */}
                    <div className="nama-pengambil-row mt-3">
                      <label>Nama Pengambil:</label>
                      <div className="nama-pengambil-input-group">
                        <input
                          type="text"
                          placeholder="Masukkan nama pengambil..."
                          value={selectedRegistration.nama_pengambil || ''}
                          onChange={(e) => handleMerchandisePickupUpdate(selectedRegistration.id, selectedRegistration.merchandise_pickup_status || 'not_ready', e.target.value)}
                          className="nama-pengambil-input"
                        />
                        {selectedRegistration.nama_pengambil && (
                          <button
                            onClick={() => handleMerchandisePickupUpdate(selectedRegistration.id, selectedRegistration.merchandise_pickup_status || 'not_ready', '')}
                            className="clear-pengambil-btn"
                            title="Hapus nama pengambil"
                          >
                            <X size={14} weight="bold" />
                          </button>
                        )}
                      </div>
                      {selectedRegistration.tanggal_pengambilan && (
                        <p className="tanggal-pengambilan-text">
                          <Hourglass size={12} weight="fill" style={{verticalAlign: 'middle', marginRight: '4px'}} /> 
                          Diambil tanggal: {new Date(selectedRegistration.tanggal_pengambilan).toLocaleDateString('id-ID', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Music Minus One File - Only for Nasyid */}
              {selectedRegistration.jenis_lomba === 'nasyid' && (
                <div className="detail-section">
                  <h3><MusicNote size={18} weight="fill" style={{verticalAlign: 'middle', marginRight: '6px'}} /> File Music Minus One (Opsional)</h3>
                  <p className="text-sm text-gray-500 mb-3">
                    Peserta Nasyid dapat mengirimkan file music minus one untuk lagu yang akan dibawakan.
                  </p>
                  
                  <div className="music-minus-one-section">
                    <div className="status-control-row">
                      <label>Status File:</label>
                      <select
                        value={selectedRegistration.music_minus_one_status || 'not_submitted'}
                        onChange={(e) => handleMusicMinusOneUpdate(selectedRegistration.id, e.target.value)}
                        className={`music-status-select ${selectedRegistration.music_minus_one_status || 'not_submitted'}`}
                      >
                        <option value="not_submitted">Belum Dikirim</option>
                        <option value="submitted">Sudah Dikirim</option>
                        <option value="verified">Terverifikasi</option>
                      </select>
                    </div>
                    
                    <div className="link-control-row mt-3">
                      <label>Link File (Google Drive, etc):</label>
                      <div className="link-input-group">
                        <input
                          type="text"
                          placeholder="https://drive.google.com/..."
                          value={selectedRegistration.music_minus_one_link || ''}
                          onChange={(e) => handleMusicMinusOneUpdate(selectedRegistration.id, selectedRegistration.music_minus_one_status || 'submitted', e.target.value)}
                          className="link-input"
                        />
                        {selectedRegistration.music_minus_one_link && (
                          <a
                            href={selectedRegistration.music_minus_one_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="open-link-button"
                          >
                            <Link size={14} weight="bold" style={{verticalAlign: 'middle', marginRight: '4px'}} /> Buka
                          </a>
                        )}
                      </div>
                    </div>
                    
                    <div className="status-badge-row mt-3">
                      <span className={`music-status-badge ${selectedRegistration.music_minus_one_status || 'not_submitted'}`}>
                        {selectedRegistration.music_minus_one_status === 'verified' ? <><CheckCircle size={14} weight="fill" style={{verticalAlign: 'middle', marginRight: '4px'}} /> Terverifikasi</> : 
                         selectedRegistration.music_minus_one_status === 'submitted' ? <><PaperPlaneRight size={14} weight="fill" style={{verticalAlign: 'middle', marginRight: '4px'}} /> Sudah Dikirim</> : 
                         <><Hourglass size={14} weight="fill" style={{verticalAlign: 'middle', marginRight: '4px'}} /> Belum Dikirim</>}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="detail-section">
                <h3>Ringkasan Biaya</h3>
                <div className="cost-breakdown">
                  <div className="cost-item">
                    <span>Biaya Pendaftaran:</span>
                    <span>{formatCurrency(selectedRegistration.jenis_lomba === 'hadroh' ? 150000 : 75000)}</span>
                  </div>
                  {selectedRegistration.jersey_design && (
                    <div className="cost-item">
                      <span>Jersey (Pre-order):</span>
                      <span>
                        {formatCurrency(
                          selectedRegistration.jersey_details.reduce((total, item) => total + (parseInt(item.jumlah) || 0), 0) * 115000
                        )}
                      </span>
                    </div>
                  )}
                  {selectedRegistration.topi_pre_order && (
                    <div className="cost-item">
                      <span>Topi (Pre-order):</span>
                      <span>{formatCurrency(selectedRegistration.topi_jumlah * 115000)}</span>
                    </div>
                  )}
                  <div className="cost-item total">
                    <span>Total:</span>
                    <span>{formatCurrency(selectedRegistration.total_biaya)}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Status & Metadata</h3>
                <div className="detail-grid">
                  <div>
                    <label>Status:</label>
                    <select
                      value={selectedRegistration.status}
                      onChange={(e) => handleStatusChange(selectedRegistration.id, e.target.value)}
                      className={`status-select ${getStatusBadgeClass(selectedRegistration.status)}`}
                    >
                      <option value="pending">Menunggu</option>
                      <option value="paid">Sudah Bayar</option>
                      <option value="confirmed">Terkonfirmasi</option>
                      <option value="cancelled">Dibatalkan</option>
                    </select>
                  </div>
                  <div>
                    <label>Tanggal Daftar:</label>
                    <p>{formatDate(selectedRegistration.created_at)}</p>
                  </div>
                  <div>
                    <label>Terakhir Diupdate:</label>
                    <p>{formatDate(selectedRegistration.updated_at)}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => handleDelete(selectedRegistration.id)} className="delete-button">
                <Trash size={16} weight="bold" style={{marginRight: '4px', verticalAlign: 'middle'}} /> Hapus Pendaftaran
              </button>
              <button onClick={closeDetailModal} className="close-modal-button">
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ICHarmonyAdmin;
